import React, { useState, useLayoutEffect, useRef, useMemo } from 'react';
import Container from './Container';
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const ContactFAQ = () => {
  const [openIndex, setOpenIndex] = useState(1); // Second FAQ is open by default
  const containerRef = useRef(null);
  const panelRefs = useRef({});
  const prevOpenRef = useRef(1);

  const faqs = useMemo(() => [
    {
      q: 'What logistics services does Timeline Express offer?',
      a: 'We provide Air, Rail, and Surface logistics (LTL & FTL), plus value-added services like consolidation, reverse logistics, and appointment-based deliveries.'
    },
    {
      q: 'How can I track my shipment?',
      a: 'You can easily track your shipment using our real-time tracking system. Simply enter your tracking ID on our website or contact our support team via WhatsApp for instant updates.'
    },
    {
      q: 'Do you offer pan-India coverage?',
      a: 'Yes, we operate a robust network supporting deliveries across India.'
    },
    {
      q: 'Do you handle urgent or express deliveries?',
      a: 'Yes, we specialize in urgent movements and time-bound deliveries.'
    }
  ], []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  useLayoutEffect(() => {
    // Initialize panels
    faqs.forEach((_, idx) => {
      const el = panelRefs.current[idx];
      if (!el) return;
      gsap.set(el, { overflow: 'hidden' });
      if (idx === openIndex) {
        gsap.set(el, { height: 'auto', opacity: 1, pointerEvents: 'auto' });
      } else {
        gsap.set(el, { height: 0, opacity: 0, pointerEvents: 'none' });
      }
    });
    prevOpenRef.current = openIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Heading animation on scroll
      const headingWords = containerRef.current?.querySelectorAll('.faq-section h2 [data-animated-word]');
      if (headingWords && headingWords.length && containerRef.current) {
        gsap.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform', scrollTrigger: { trigger: containerRef.current, start: 'top 80%', toggleActions: 'play none none none', once: true } });
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  useLayoutEffect(() => {
    const prev = prevOpenRef.current;
    if (prev === openIndex) return;

    // Close previous
    if (prev !== -1 && panelRefs.current[prev]) {
      const prevEl = panelRefs.current[prev];
      gsap.killTweensOf(prevEl);
      const currentHeight = prevEl.scrollHeight;
      gsap.set(prevEl, { height: currentHeight, overflow: 'hidden' });
      gsap.to(prevEl, { height: 0, opacity: 0, duration: 0.35, ease: 'power2.inOut', onComplete: () => gsap.set(prevEl, { pointerEvents: 'none' }) });
    }

    // Open current
    if (openIndex !== -1 && panelRefs.current[openIndex]) {
      const curEl = panelRefs.current[openIndex];
      gsap.killTweensOf(curEl);
      gsap.set(curEl, { overflow: 'hidden', pointerEvents: 'auto' });
      const autoHeight = curEl.scrollHeight || 'auto';
      gsap.fromTo(curEl, {
        height: 0,
        opacity: 0
      }, {
        height: autoHeight,
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out',
        onComplete: () => gsap.set(curEl, { height: 'auto' })
      });
    }

    prevOpenRef.current = openIndex;
  }, [openIndex]);

  return (
    <section className="section faq-section alt" ref={containerRef}>
      <Container>
        <div className='flex flex-col items-center'>

          <div className="container section-badge flex justify-center items-center gap-3 text-[16px] md:text-[20px]">
            <div className="badge-dot"></div>
            FAQs
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl"><AnimatedWords auto={false} lines={["People You Can Rely On."]} /></h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
                <button
                  className="faq-toggle !p-2"
                  aria-expanded={openIndex === index ? 'true' : 'false'}
                  onClick={() => toggleFAQ(index)}
                >
                  <span className="faq-question text-sm md:text-base lg:text-lg">{faq.q}</span>
                  <span className="faq-icon">{openIndex === index ? '–' : '+'}</span>
                </button>
                <div className="faq-panel" ref={(el) => { panelRefs.current[index] = el; }}>
                  <p className="text-sm md:text-base">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ContactFAQ;
